import { useContext, useEffect, useRef, useState } from 'react';
import { IconContext } from 'react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaHome } from 'react-icons/fa';
import { FaRegUser, FaChevronDown, FaUser } from 'react-icons/fa6';
import { IoInformationCircle } from 'react-icons/io5';
import { FaRegUserCircle } from 'react-icons/fa';
import { PiPersonArmsSpreadFill, PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { FiExternalLink, FiLogOut } from 'react-icons/fi';
import Popup from 'reactjs-popup';
import Cookies from 'js-cookie';
import PlacesSelector from './PlacesSelector';
import { GetRouteData } from 'data/routeData';
import { usePerms } from 'contexts/PermsContext';
import useTextToSpeech from 'hooks/useTextToSpeech';
import { clearCookies } from 'utils/cookieUtils';
import { ColorContext } from 'App';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import styles from './assets/header.module.css';
import { Button, Container } from 'react-bootstrap';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { logout } from 'services/auth/auth.service';

export default function Header({
  widgetRefresh,
  setWidgetRefresh,
  setShowFull,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [routeData, setRouteData] = useState(null);
  const { instance } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const perms = usePerms();
  const tts = useTextToSpeech();
  const theme = useContext(ColorContext);
  const widthRef = useRef(window.innerWidth);
  const authContext = useContext(AuthContext);
  const { logout: authLogout } = authContext;

  const handleLogout = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    setShowFull(true);

    // Call API logout
    logout();

    // Keep remember me data if set
    let keepCookies = [];
    if (Cookies.get('rememberMe') === 'true') {
      keepCookies = ['rememberMe', 'email'];
    }
    clearCookies(keepCookies);

    // Explicitly clear privacy and cookie agreement for legal compliance
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    setCookiesAgreed(false);
    setPrivacyAgreed(false);

    // Reset app state
    perms.reset();
    tts.cancel();
    setNewTermsFullyAgreed(false);
    setTermsFullyViewed(false);
    setHasSignedIn(false);
    setHasFetchedPerms(false);

    // Use centralized auth logout
    authLogout();

    navigate('/');
  };

  useEffect(() => {
    window.addEventListener('resize', () => (widthRef.current = window.innerWidth));
  }, []);

  useEffect(() => {
    let data;

    if (location.pathname === '/dashboard') {
      data = { icon: () => <FaHome />, displayName: 'Dashboard' };
    } else if (location.pathname === '/vms') {
      data = { icon: () => <PiVideoFill />, displayName: 'Overwatch VMS' };
    } else if (location.pathname === '/maintenancedashboard') {
      data = {
        icon: () => <PiWrenchFill />,
        displayName: 'Maintenance Devices',
      };
    } else {
      const sideBarData = GetRouteData(perms);

      sideBarData.forEach((section) => {
        section.options.forEach((option) => {
          if (option.href === location.pathname)
            data = { icon: option.icon, displayName: option.name };
        });
      });
    }

    setRouteData(data);
  }, [location.pathname]);

  return (
    <Container
      as="header"
      fluid
      className="d-flex flex-column flex-xl-row align-items-center justify-content-between py-4 gap-3 w-100"
      style={{
        backgroundColor: theme.primaryShadow,
        color: theme.fontColor,
      }}
    >
      {routeData && (
        // Page Title and Icon
        <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
          <IconContext.Provider value={{ color: theme.fontColor, size: 45 }}>
            <div>{routeData.icon()}</div>
          </IconContext.Provider>
          <div className="gap-3 gap-sm-4 d-flex align-items-center justify-content-center">
            <div className="fs-1 text-center">{routeData.displayName}</div>
          </div>
        </div>
      )}
      {/* Location Selector */}
      <div className="d-flex flex-column flex-xl-row align-items-center gap-3 me-3">
        {!location.pathname.includes('usermenu') && (
          <Button
            className={`d-flex me-3 justify-content-between align-items-center fs-4 ${styles['rounded-50']}`}
            variant="primary"
          >
            <PlacesSelector widgetRefresh={widgetRefresh} setWidgetRefresh={setWidgetRefresh} />
          </Button>
        )}
        {/* Overwatch Community Link */}
        <Button
          onClick={() => window.open('http://www.overwatch.ai/', '_blank', 'noreferrer')}
          className={styles['rounded-50']}
          variant="primary"
        >
          <div
            className="d-flex align-items-center justify-content-start fs-6"
            style={{
              width: 200,
              height: 32,
            }}
          >
            <div>
              <div
                className={`d-flex align-items-center justify-content-center ${styles['icon-background']}`}
                style={{
                  backgroundColor: theme.background,
                  marginLeft: 3,
                  width: 27,
                  height: 27,
                }}
              >
                <IconContext.Provider
                  value={{
                    color: theme.fontColor,
                    size: 12,
                  }}
                >
                  <FiExternalLink />
                </IconContext.Provider>
              </div>
            </div>
            <div style={{ marginLeft: 5 }}>Overwatch Community</div>
          </div>
        </Button>
        <Button className={`me-3 ${styles['rounded-50']}`} variant="primary">
          {/* User Menu Dropdown */}
          <Popup
            on="hover"
            mouseLeaveDelay={300}
            trigger={
              <div>
                <div
                  className={`d-flex fs-6 align-items-center justify-content-start ${styles['rounded-50']}`}
                  style={{
                    backgroundColor: theme.primaryHighlight,
                    width: 175,
                    height: 32,
                  }}
                >
                  <div>
                    <div
                      className={`d-flex align-items-center justify-content-center ${styles['rounded-50']}`}
                      style={{
                        marginLeft: 3,
                        backgroundColor: theme.background,
                        width: 27,
                        height: 27,
                      }}
                    >
                      <IconContext.Provider
                        value={{
                          color: theme.fontColor,
                          size: 12,
                        }}
                      >
                        <FaRegUser />
                      </IconContext.Provider>
                    </div>
                  </div>
                  <div style={{ marginLeft: 5 }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>User Menu</div>
                      <div style={{ marginLeft: 40, marginTop: 5 }}>
                        <IconContext.Provider
                          value={{
                            color: theme.fontColor,
                            size: 15,
                          }}
                        >
                          <FaChevronDown />
                        </IconContext.Provider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            position="bottom right"
            closeOnDocumentClick
            closeOnEscape
          >
            <div
              className="d-flex flex-column align-items-center justify-content-center border border-1 border-black"
              style={{
                backgroundColor: '#2c486d',
                color: theme.fontColor,
                height: 165,
              }}
            >
              <div
                className="w-100 d-flex align-items-center border-bottom border-1 border-secondary pe-1"
                style={{ backgroundColor: '#1b3047' }}
              >
                <div
                  className="mx-3 mt-1"
                  style={{
                    justifySelf: 'flex-start',
                  }}
                >
                  <IconContext.Provider
                    value={{
                      color: theme.fontColor,
                      size: 20,
                    }}
                  >
                    <FaRegUserCircle />
                  </IconContext.Provider>
                </div>
                <div className="d-flex flex-column">
                  <div>Signed in as:</div>
                  <div> {Cookies.get('username')}</div>
                </div>
              </div>
              <a
                className="d-flex w-100 align-items-center border-1 border-bottom border-secondary text-white"
                href={'/usermenu?tab=Account'}
              >
                <div className="mt-1 mx-3" style={{ justifySelf: 'flex-start' }}>
                  <IconContext.Provider value={{ color: theme.fontColor, size: 20 }}>
                    <FaUser />
                  </IconContext.Provider>
                </div>
                <div>Edit Account</div>
              </a>
              <a
                className="d-flex w-100 align-items-center border-1 border-bottom border-secondary text-white"
                href={'/usermenu?tab=Accessibility'}
              >
                <div className="mt-1 mx-3" style={{ justifySelf: 'flex-start' }}>
                  <IconContext.Provider value={{ color: theme.fontColor, size: 20 }}>
                    <PiPersonArmsSpreadFill />
                  </IconContext.Provider>
                </div>
                <div>Accessibility Settings</div>
              </a>
              <a
                className="w-100 d-flex align-items-center border-1 border-bottom border-secondary text-white"
                href={'/usermenu?tab=About'}
              >
                <div className="mt-1 mx-3" style={{ justifySelf: 'flex-start' }}>
                  <IconContext.Provider value={{ color: theme.fontColor, size: 20 }}>
                    <IoInformationCircle />
                  </IconContext.Provider>
                </div>
                <div>About</div>
              </a>
              <button className="w-100 d-flex align-items-center" onClick={() => handleLogout()}>
                <div className="mt-1 mx-3" style={{ justifySelf: 'flex-start' }}>
                  <IconContext.Provider value={{ color: theme.fontColor, size: 20 }}>
                    <FiLogOut />
                  </IconContext.Provider>
                </div>
                <div>Logout</div>
              </button>
            </div>
          </Popup>
        </Button>
      </div>
    </Container>
  );
}
